import React, { useState, useEffect, createContext, useRef, useContext } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import ChessLobby from './components/ChessLobby';
import ChessGame from './components/ChessGame';
import UserProfile from './components/UserProfile';
import Login from './components/Login';
import SpectatorMode from './components/SpectatorMode';
import AnimatedFavicon from './components/AnimatedFavicon';
import { SoundProvider } from './components/SoundManager';
import {
  ThemeProvider,
  useTheme,
  ThemeSwitch,
  ThemedContainer,
  ThemedButton,
  woodStyles
} from './components/ThemeContext';
import { API_URL, endpoint } from './apiConfig';

// Create AuthContext
export const AuthContext = createContext(null);

// Setup axios defaults
axios.defaults.baseURL = API_URL;

// Create axios interceptor to add token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const createAnonymousUser = async () => {
  const anonymousId = `anon_${Math.random().toString(36).substr(2, 9)}`;
  try {
    const response = await axios.post('/api/anon', { anonymousId });
    //console.log('API Response for anon user:', response.data);

    // Check if it's a fallback response (server error with fallback data)
    if (response.data.fallback === true) {
      //console.log('Using local-only mode due to server error:', response.data.message);
      
      // Create a local-only fallback user with isLocalOnly flag
      const fallbackUser = {
        id: response.data.user?.id || anonymousId,
        username: response.data.user?.username || 'Guest',
        isAnonymous: true,
        isLocalOnly: true 
      };
      
      // Store in localStorage
      localStorage.setItem('gameUser', JSON.stringify(fallbackUser));
      return fallbackUser;
    }

    // Normal successful response with token
    const token = response.data.token;
    //console.log('Token received:', token);
    
    if (token) {
      localStorage.setItem('token', token);
      return {
        id: response.data.user?.id || anonymousId,
        username: response.data.user?.username || 'Guest',
        isAnonymous: true,
        token: token
      };
    } else {
      console.warn('No token in response - using local-only mode');
      const fallbackUser = {
        id: response.data.user?.id || anonymousId,
        username: response.data.user?.username || 'Guest',
        isAnonymous: true,
        isLocalOnly: true // This is critical!
      };
      localStorage.setItem('gameUser', JSON.stringify(fallbackUser));
      return fallbackUser;
    }
  } catch (error) {
    console.error('Failed to create anonymous user:', error);
    
    // FALLBACK: Create a local-only anonymous user when API fails
    const fallbackUser = {
      id: anonymousId,
      username: 'Guest',
      isAnonymous: true,
      isLocalOnly: true // This is critical!
    };
    
    // Store in localStorage
    localStorage.setItem('gameUser', JSON.stringify(fallbackUser));
    return fallbackUser;
  }
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const initializationRef = useRef(false);

  const validateToken = async () => {
    try {
      const response = await axios.get('/api/auth/validate');
      return response.data.valid;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const initializeUser = async () => {
      if (initializationRef.current) return;
      initializationRef.current = true;
      try {
        const savedToken = localStorage.getItem('token');
        const savedUser = localStorage.getItem('gameUser');
  
        if (savedToken && savedUser) {
          try {
            // Only validate token if it's not a local-only user
            const parsedUser = JSON.parse(savedUser);
            if (!parsedUser.isLocalOnly) {
              const isValid = await validateToken();
              if (isValid) {
                setUser(parsedUser);
                axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
              } else {
                await createAndSetAnonymousUser();
              }
            } else {
              // For local-only users, just use them without validation
              setUser(parsedUser);
            }
          } catch (error) {
            await createAndSetAnonymousUser();
          }
        } else {
          await createAndSetAnonymousUser();
        }
      } catch (error) {
        console.error('Error initializing user:', error);
        
        // Final fallback - create a simple guest user if everything else fails
        const emergencyUser = {
          id: `emergency_${Date.now()}`,
          username: 'Emergency Guest',
          isAnonymous: true,
          isLocalOnly: true,
          isEmergencyFallback: true
        };
        setUser(emergencyUser);
        localStorage.setItem('gameUser', JSON.stringify(emergencyUser));
      } finally {
        setIsLoading(false);
      }
    };
  
    const createAndSetAnonymousUser = async () => {
      const anonymousUser = await createAnonymousUser();
      if (anonymousUser) {
        setUser(anonymousUser);
        localStorage.setItem('gameUser', JSON.stringify(anonymousUser));
      }
    };
  
    initializeUser();
  }, []);
  
  const login = (userData) => {
    setUser(userData);
    localStorage.setItem('token', userData.token);
    localStorage.setItem('gameUser', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];

      const anonymousUser = await createAnonymousUser();
      if (anonymousUser) {
        localStorage.setItem('gameUser', JSON.stringify(anonymousUser));
        setUser(anonymousUser);
        window.location.href = '/lobby';
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const authContextValue = {
    user,
    login,
    logout,
    isAuthenticated: !!user && !user.isAnonymous,
    token: localStorage.getItem('token'),
    isLocalOnly: user?.isLocalOnly || false // Add this flag to the context
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Navigation component with mobile responsiveness
const Navigation = () => {
  const { woodStyles } = useTheme();
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="fixed w-full z-10">
      <nav className="border-b border-opacity-20" style={{ borderColor: woodStyles.borderColor }}>
        <ThemedContainer>
          <div className="px-2 py-1">
            <div className="max-w-7xl mx-auto">
              {/* Main navbar content */}
              <div className="flex items-center justify-between">
                {/* Logo */}
                <div className="flex flex-col">
                  <Link to="/lobby" className={`text-2xl font-bold ${woodStyles.text.primary} flex-shrink-0`}>
                    Slot♘Chess♞
                  </Link>
                  <span className={`text-xs italic ${woodStyles.text.primary} -mt-1 text-center`}>
                    Chess with a chance!
                  </span>
                </div>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center space-x-4">
                  <div className="flex space-x-4">
                    <Link
                      to="/lobby"
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/lobby'
                          ? woodStyles.button.primary
                          : `${woodStyles.text.secondary} hover:${woodStyles.text.primary}`
                        }`}
                    >
                      🎮 Lobby
                    </Link>
                    <Link
                      to="/profile"
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/profile'
                          ? woodStyles.button.primary
                          : `${woodStyles.text.secondary} hover:${woodStyles.text.primary}`
                        }`}
                    >
                      📊 Profile
                    </Link>
                    <Link
                      to="/spectate"
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/spectate'
                          ? woodStyles.button.primary
                          : `${woodStyles.text.secondary} hover:${woodStyles.text.primary}`
                        }`}
                    >
                      👓 Spectate
                    </Link>
                  </div>
                  <ThemeSwitch />
                  {user?.isAnonymous ? (
                    <Link
                      to="/login"
                      className={`${woodStyles.button.primary} px-4 py-2 rounded text-white`}
                    >
                      Sign In
                    </Link>
                  ) : (
                    <div className="flex items-center space-x-4">
                      <span className={`text-sm ${woodStyles.text.secondary}`}>
                        👤 {user.username}
                      </span>
                      <ThemedButton
                        onClick={logout}
                        variant="secondary"
                      >
                        Logout
                      </ThemedButton>
                    </div>
                  )}
                </div>

                {/* Mobile menu button */}
                <div className="md:hidden">
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className={`${woodStyles.text.secondary} hover:${woodStyles.text.primary} p-2 rounded-md focus:outline-none`}
                  >
                    {isMenuOpen ? (
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    ) : (
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Mobile menu */}
              <div className={`md:hidden ${isMenuOpen ? 'block' : 'hidden'}`}>
                <div className="px-2 pt-2 pb-3 space-y-1">
                  <Link
                    to="/lobby"
                    onClick={() => setIsMenuOpen(false)}
                    className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/lobby'
                        ? woodStyles.button.primary
                        : `${woodStyles.text.secondary} hover:${woodStyles.text.primary}`
                      }`}
                  >
                    🎮 Lobby
                  </Link>
                  <Link
                    to="/profile"
                    onClick={() => setIsMenuOpen(false)}
                    className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/profile'
                        ? woodStyles.button.primary
                        : `${woodStyles.text.secondary} hover:${woodStyles.text.primary}`
                      }`}
                  >
                    📊 Profile
                  </Link>
                  <Link
                    to="/spectate"
                    onClick={() => setIsMenuOpen(false)}
                    className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/spectate'
                        ? woodStyles.button.primary
                        : `${woodStyles.text.secondary} hover:${woodStyles.text.primary}`
                      }`}
                  >
                    👓 Spectate
                  </Link>
                  <div className="px-3 py-2">
                    <ThemeSwitch />
                  </div>
                  <div className="px-3 py-2">
                    {user?.isAnonymous ? (
                      <Link
                        to="/login"
                        onClick={() => setIsMenuOpen(false)}
                        className={`block w-full text-center ${woodStyles.button.primary} px-4 py-2 rounded text-white`}
                      >
                        Sign In
                      </Link>
                    ) : (
                      <div className="space-y-2">
                        <div className={`text-sm ${woodStyles.text.secondary}`}>
                          👤 {user.username}
                        </div>
                        <ThemedButton
                          onClick={() => {
                            logout();
                            setIsMenuOpen(false);
                          }}
                          variant="secondary"
                          className="w-full"
                        >
                          Logout
                        </ThemedButton>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ThemedContainer>
      </nav>
    </header>
  );
};

// Themed App component

const App = () => {
  return (
    
    <Router>
      <ThemeProvider>
       <AnimatedFavicon />
        <ThemedAppContent />
      </ThemeProvider>
    </Router>
  );
};

// Themed App Content
const ThemedAppContent = () => {
  return (
    <AuthProvider>
      <SoundProvider>
        <ThemedContainer className="min-h-screen flex flex-col">
          <Navigation />
          <main className="flex-1 flex flex-col pt-16">
            <div className="flex-1 container mx-auto px-4">
              <Routes>
                <Route path="/lobby" element={<ChessLobby />} />
                <Route path="/game/:gameId" element={<ChessGame />} />
                <Route path="/game_local/:gameId" element={<ChessGame />} />
                <Route path="/profile" element={<UserProfile />} />
                <Route path="/login" element={<Login />} />
                <Route path="/spectate" element={<SpectatorMode />} />
                <Route path="/" element={<Navigate to="/lobby" replace />} />
              </Routes>
            </div>
          </main>
        </ThemedContainer>
      </SoundProvider>
    </AuthProvider>
  );
};


export default App;